import { useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { guardUnsavedBack } from '../../../lib/formGuard';
import type { AllergiesMeds } from '../../../types/prf';

interface AllergiesMedsSectionProps {
  data: AllergiesMeds;
  onSave: (data: AllergiesMeds) => void;
  onBack: () => void;
}

export default function AllergiesMedsSection({
  data,
  onSave,
  onBack,
}: AllergiesMedsSectionProps) {
  const [formData, setFormData] = useState<AllergiesMeds>(data);

  const handleChange = (field: keyof AllergiesMeds, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field: keyof AllergiesMeds, value: string) => {
    // Split textarea value by newlines into an array, filter empty lines
    const arr = value.split('\n');
    setFormData(prev => ({ ...prev, [field]: arr }));
  };

  // Helper to safely render an array field as joined string (handles legacy string data)
  const arrayToString = (val: string[] | string | undefined): string => {
    if (Array.isArray(val)) return val.join('\n');
    if (typeof val === 'string') return val;
    return '';
  };

  const handleSave = () => {
    onSave(formData);
  };

  const handleBackClick = () => guardUnsavedBack(data, formData, onBack);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackClick}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-foreground">Allergies/Meds</h2>
              <p className="text-xs text-muted-foreground">Allergies and current medications</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Allergies */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Allergies
            </label>
            <textarea
              value={formData.additionalNotes || ''}
              onChange={(e) => handleChange('additionalNotes', e.target.value)}
              rows={6}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              placeholder="List any known allergies (medications, food, environmental, etc.)"
            />
          </div>

          {/* Medication */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Medication
            </label>
            <textarea
              value={arrayToString(formData.currentMedications)}
              onChange={(e) => handleArrayChange('currentMedications', e.target.value)}
              rows={6}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              placeholder="List current medications including dosage and frequency"
            />
          </div>
        </div>
      </div>

      {/* Footer with Save Button */}
      <div className="flex-shrink-0 border-t border-border bg-card p-4">
        <button
          onClick={handleSave}
          className="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
      </div>
    </div>
  );
}
